/** Screen-flow state machine for the till session.
 *
 * The till flow is linear and kiosk-style, so we model "which screen" as an explicit state
 * machine (a pure reducer) rather than a router. TanStack Query owns the actual fetching;
 * query results dispatch events here to drive the transition.
 *
 * Flow: validating → idle → pointsGiven → customerInfo. The `pointsGiven` screen doubles as the
 * loading state: it spins while the customer loads + the scan points are awarded, then resolves
 * into the checkmark. From customerInfo a redemption goes to `redeemed`, then back to customerInfo.
 */

export type FlowStep =
  | 'validating' // resolving webext_uuid on load
  | 'idle' // validated; waiting for a customer scan
  | 'pointsGiven' // scanned: load customer + award points (spinner → checkmark)
  | 'customerInfo' // customer info: progress, give another point, available rewards
  | 'redeemed' // reward redeemed — celebration screen (returns to customerInfo)
  | 'error'

/** Reward + customer shown on the redemption celebration screen. */
export interface Redemption {
  rewardLabel: string
  customerName: string
}

export interface FlowState {
  step: FlowStep
  error: string | null
  /** The §13.8 code behind `error`, when there was one.
   *
   * Carried separately from the message because the fallback copy tells the cashier to
   * "report the code below" -- and until this field existed there was no code below.
   * The message was chosen FROM the code and the code was then dropped on the floor, so
   * the screen asked for the one thing the app had just discarded. */
  errorCode?: string | null
  redemption?: Redemption | null
}

export type FlowEvent =
  | { type: 'VALIDATED' }
  | { type: 'VALIDATE_FAILED'; message: string; code?: string }
  | { type: 'SCAN' }
  | { type: 'CUSTOMER_FAILED'; message: string; code?: string }
  | { type: 'SHOW_INFO' }
  | { type: 'REDEEMED'; redemption: Redemption }
  | { type: 'SUBMIT_FAILED'; message: string; code?: string }
  | { type: 'RESET' }

export const initialFlowState: FlowState = { step: 'validating', error: null, errorCode: null }

export function flowReducer(state: FlowState, event: FlowEvent): FlowState {
  switch (event.type) {
    case 'VALIDATED':
      return state.step === 'validating' ? { step: 'idle', error: null, errorCode: null } : state
    case 'VALIDATE_FAILED':
      return { step: 'error', error: event.message, errorCode: event.code ?? null }
    case 'SCAN':
      return { step: 'pointsGiven', error: null, errorCode: null }
    case 'CUSTOMER_FAILED':
      return { step: 'error', error: event.message, errorCode: event.code ?? null }
    case 'SHOW_INFO':
      return { step: 'customerInfo', error: null, errorCode: null }
    case 'REDEEMED':
      return { step: 'redeemed', error: null, errorCode: null, redemption: event.redemption }
    case 'SUBMIT_FAILED':
      return { step: 'error', error: event.message, errorCode: event.code ?? null }
    case 'RESET':
      return { step: 'idle', error: null, errorCode: null, redemption: null }
    default:
      return state
  }
}
